import { fetchChfarTripDetails, logger } from "@hopper-b2b/api";
import { FlightShopType, type TripDetails } from "@hopper-b2b/types";
import { put, select } from "redux-saga/effects";

import { fetchTripDetails as fetchDetails } from "../../../api/v1/shop/fetchTripDetails";
import Logger from "../../../helpers/Logger";
import { flightApiConfigStoreKey } from "../../../reducers/types";
import type { IStoreState } from "../../../reducers/types";
import { actions } from "../actions";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isAuthError(e: unknown): boolean {
  if (e && typeof e === "object" && "response" in e) {
    const response = (e as { response?: { status?: number } }).response;
    return response?.status === 401;
  }
  return false;
}

export function* fetchTripDetails(action: actions.IFetchTripDetails) {
  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const state: IStoreState = yield select();
      const apiConfig = state[flightApiConfigStoreKey];
      const chfarId = state.flightShop.chfarId;
      const isChfarFlightShop =
        state.flightShop.flightShopType === FlightShopType.CHFAR_EXERCISE &&
        chfarId;

      const tripDetails: TripDetails = isChfarFlightShop
        ? yield fetchChfarTripDetails(action.tripId, chfarId, apiConfig)
        : yield fetchDetails(action.tripId, apiConfig);
      yield put(actions.setTripDetails(tripDetails));
      return;
    } catch (e) {
      lastError = e;
      Logger.debug(e);

      if (isAuthError(e)) {
        logger()?.error(
          `fetchTripDetails failed for tripId=${action.tripId} due to auth error (401). Session may have expired.`,
          {
            tripId: action.tripId,
            error: e instanceof Error ? e.message : String(e),
          }
        );
        yield put(
          actions.setTripDetailsError(
            action.tripId,
            e instanceof Error ? e.message : "Authentication error"
          )
        );
        return;
      }

      if (attempt < MAX_RETRIES) {
        logger()?.warn(
          `fetchTripDetails failed for tripId=${action.tripId}, attempt ${attempt + 1}/${MAX_RETRIES + 1}. Retrying...`,
          {
            tripId: action.tripId,
            attempt: attempt + 1,
            error: e instanceof Error ? e.message : String(e),
          }
        );
        yield delay(RETRY_DELAY_MS * (attempt + 1));
      }
    }
  }

  const errorMessage =
    lastError instanceof Error ? lastError.message : String(lastError);
  logger()?.error(
    `fetchTripDetails failed for tripId=${action.tripId} after ${MAX_RETRIES + 1} attempts.`,
    {
      tripId: action.tripId,
      error: errorMessage,
    }
  );
  yield put(actions.setTripDetailsError(action.tripId, errorMessage));
}
